<template>
  <div class="container">
    <!-- Calendar Left Panel -->
    <vue-resizable
      v-show="showLeftPanel"
      class="left-panel"
      :class="{ 'full-width': showLeftPanel }"
      style="flex: 0.18 1 auto; margin: 10px"
      :min-width="!isMobile ? 200 : null"
      :max-width="!isMobile ? 200 : null"
    >
      <CaledarLeftPanel @toggleLeftPanel="toggleLeftPanel" />
    </vue-resizable>
    <!-- Calendar Center Panel -->
    <div
      v-show="!isMobile || (isMobile && !showLeftPanel)"
      class="center-panel"
      :class="{ 'full-width': isMobile }"
      style="flex: 1 1 auto; height: 100vh; padding-top: var(--safe-area-inset-top); padding-bottom: var(--safe-area-inset-bottom); padding-left: var(--safe-area-inset-left); padding-right: var(--safe-area-inset-right);"
    >
      <div style="display: flex;">
       <v-toolbar v-if="hasCalendar" flat density="compact" color="white" class="px-4">
          <!-- Left Menu Toggle -->
          <template v-slot:prepend>
            <v-icon size="30" @click="toggleLeftPanel" class="me-2">
              {{ showLeftPanel ? 'mdi-menu-left-outline' : 'mdi-menu-right-outline' }}
            </v-icon>
            <span class="text-h6" v-if="!isMobile">{{ currentCalendar.name }}</span>
          </template>

          <!-- Filter Selects -->
          <template v-slot:append>
            <div class="d-flex align-center flex-wrap" style="gap: 5px; margin-bottom: 5px;">
              <v-btn 
                v-if="isMobile"
                icon
                variant="plain"
                class="mt-2"
              > 
                <v-icon size="30" :color="currentCalendar.color">mdi-filter</v-icon>
                  <v-menu activator="parent" :close-on-content-click="false">
                      <template v-slot:default="{ isActive }">
                         <v-card style="width: 300px">
                        <v-card-title class="d-flex justify-space-between align-center">
                           <div class="d-flex align-center">
                             <v-icon left color="primary" >mdi-calendar-clock</v-icon>
                               <span class="text-h6 font-weight-light mr-3">Filters</span>
                           </div>
                           <v-icon size="20" color="red" class="clickable" @click="isActive.value=false">mdi-close</v-icon>
                        </v-card-title>
                        <v-divider></v-divider>
                        <v-card-text>
                         <CalendarFilter 
                          :schedules="schedules"
                          v-model:selectedResourceValues="selectedResource"
                          v-model:selectedClinicValues="selectedClinics"
                          v-model:selectedDoctorValues="selecteddoctors"
                          v-model:selectedAuthorValues="selectedAuthors"
                          v-model:selectedStatusValues="selectedStatus"
                        />
                        </v-card-text>
                      </v-card>
                      </template>
                  </v-menu>
              </v-btn>
              <div v-else>
                <CalendarFilter 
                  class="d-flex align-center flex-wrap" style="gap: 5px; margin-bottom: 5px;"
                  :schedules="schedules"
                  v-model:selectedResourceValues="selectedResource"
                  v-model:selectedClinicValues="selectedClinics"
                  v-model:selectedDoctorValues="selecteddoctors"
                  v-model:selectedAuthorValues="selectedAuthors"
                  v-model:selectedStatusValues="selectedStatus"
                />
              </div>
              <v-btn 
                v-if="canEditCalendar && isAdmin"
                icon
                variant="plain"
                @click="editCalendar"
                class="mt-2"
              > 
                <v-icon size="30" :color="currentCalendar.color"> mdi-calendar-edit-outline</v-icon>
              </v-btn>
                <v-btn 
                  icon
                  variant="plain"
                  class="mt-2"
                > 
                  <v-icon  size="30" :color="currentCalendar.color">mdi-view-dashboard-outline</v-icon>
                    <v-menu activator="parent" :close-on-content-click="false">
                      <template v-slot:default="{ isActive }">
                      <v-card style="width: 300px">
                        <v-card-title class="d-flex justify-space-between align-center">
                           <div class="d-flex align-center">
                             <v-icon left color="primary" >mdi-calendar-clock</v-icon>
                               <span class="text-h6 font-weight-light mr-3">Calendar Time Range</span>
                           </div>
                           <v-icon size="20" color="red" class="clickable" @click="isActive.value=false">mdi-close</v-icon>
                        </v-card-title>
                        <v-divider></v-divider>
                        <v-card-text>
                          <div v-for="slider in sliders" :key="slider.label">
                            <v-slider
                              v-model="this[slider.model]"
                              :max="slider.max"
                              :min="slider.min"
                              :step="slider.step"
                              :label="slider.label"
                              thumb-label="always"
                              show-ticks
                              :color="currentCalendar.color"
                            ></v-slider>
                          </div>
                        </v-card-text>
                      </v-card>
                      </template>
                    </v-menu>
                </v-btn>
              </div>
          </template>
        </v-toolbar>
      </div>
       <v-progress-linear
        v-if="isLoading"
        color="green"
        indeterminate
        :height="5"
        style="margin-bottom: 1%"
      ></v-progress-linear>
      <!--  :style="{ '--vuecal-primary-color': currentCalendar.color }" -->
      <VueCal
        :time-from="timeFrom * 60"
        :time-to="timeTo * 60"
        :time-step="timeStep"
        :snap-to-interval="timeStep"
        class="vuecal--blue-theme"
        :event-create-min-drag="15"
        :editable-events="editableEvents"
        time-at-cursor
        :watchRealTime="true"
        :twelveHour="true"
        sm
        view="day"
        :views-bar="false"
        :selected-date="selectedDate"
        :viewDate="selectedDate"
        :schedules="filteredSchedules"
        :events="filteredEvents"
        @view-change="handleViewChange($event)"
        @event-create="createEvent($event)"
        @event-delete="handleDeleteEvent($event)"
        @event-resize-end="handleEventResize($event)"
        @event-dropped="handleEventDropped($event)"
        @event-delayed-click="eventClicked($event)" 
        style="height: 80vh"
        :style="{ '--vuecal-primary-color': currentCalendar.color }"
      >
        <template #schedule-heading="{ schedule }">
          <div v-if="isDSSCalendar" >
            <UserPopup :user_id="schedule.id" :dense="true"/>
          </div>
          <strong v-else :style="`color: ${schedule.color}`" >{{ schedule.label }}</strong>
        </template>
       
      </VueCal>

      <CalendarForm
        v-if="openEventForm"
        v-model="openEventForm"
        @close="openEventForm = false; newEvent=null"
        :isEvent="isEvent"
        :isCalendar="isCalendar"
        :isEdit="isEdit"
        :calendar="currentCalendar"
        :event="(isEdit) ? selectedEvent : newEvent"
      ></CalendarForm>

      <ODAppointmentCard
        v-if="isODCalendar"
        v-model="showAptDetails"
        :dialog="showAptDetails"
        :selectedEvent="selectedEvent"
        :schedules="schedules"
        @close="showAptDetails = false"
      />
     
    </div>
  </div>
</template>
<script>
import { VueCal } from "vue-cal";
import "vue-cal/style";
import { mapGetters, mapMutations, mapActions } from "vuex";
import EventBus from "@/eventBus";
import CaledarLeftPanel from "./CaledarLeftPanel.vue";
import VueResizable from "vue-resizable";
import CalendarForm from "./CalendarForm.vue";
import { CALENDAR_TYPES, EVENT_STATUS } from "@/constants/constantsValues.js";
import ColorMixin from "@/mixins/colorMixin.js";
import UserPopup from "../Avatar/UserPopup.vue";
import OdService from "../../services/odService.js"
import CalendarService from "../../services/calendarService.js";
import ODAppointmentCard from "./ODAppointmentCard.vue";
import CalendarFilter from "./CalendarFilter.vue";
import router from "@/router";
import { setCustomBackHandler, clearCustomBackHandler ,useDefaultBackHandler} from '@/backButtonManager'
export default {
  name: "Calendar",
  mixins: [ColorMixin],
  data() {
    return {
      showLeftPanel: true,
      openEventForm: false,
      isEvent: false,
      isCalendar: false,
      isEdit: false,
      selectedDate: this.dayjs().format("YYYY-MM-DD"), // Initialize with current date
      schedules: [],
      selectedResource : [],// selected Schedules
      selectedClinics : [],
      selecteddoctors : [],
      timeFrom : 0,
      timeTo : 0,
      timeStep: 30,
      editableEvents : false,
      selectedAuthors : [],
      selectedStatus : [],
      newEvent: null,
      showAptDetails : false,
      selectedEvent : {},
    };
  },
  components: {
    VueCal,
    CaledarLeftPanel,
    VueResizable,
    CalendarForm,
    UserPopup,
    ODAppointmentCard,
    CalendarFilter
    
  },
  computed: {
    ...mapGetters("window", ["isMobile"]),
    ...mapGetters("calendar", ["currentCalendar","canEditCalendar","isODCalendar","isDSSCalendar","isLoading","doctorsList","scheduleEvents","authorsList","clinics","allClinics"]),
    ...mapGetters(['isAdmin']),
    ...mapGetters("people",["getUserName"]),

    hasCalendar() {
      return this.currentCalendar && Object.keys(this.currentCalendar)?.length > 0;
    },

    filteredSchedules() {
      let result = this.schedules;

      if (this.selectedResource?.length) {
        result = result.filter(schedule =>
          this.selectedResource.includes(schedule.id)
        );
      }

      if (this.selectedClinics?.length) {
        result = result.filter(schedule =>
          this.selectedClinics.includes(schedule.clinicNum)
        );
      }
      return result;
    },
    filteredEvents() {
      if (this.filteredSchedules.length === 0) {
        return []; // If no matching schedules, block all events
      }
      let results = this.scheduleEvents;
    
      if(this.selecteddoctors?.length){
        results = results.filter(event =>
          this.selecteddoctors.includes(event.title)
        );
      }
      if(this.selectedAuthors?.length){
        results = results.filter(event =>
          this.selectedAuthors.includes(event.author)
        );
      }
      if(this.selectedStatus?.length){
        results = results.filter(event =>
          this.selectedStatus.includes(event.status)
        );
      }
      return results;
    },
    eventStatus(){
      return Object.values(EVENT_STATUS);
    },
   sliders() {
      return [
        {
          model: 'timeFrom',
          max: 12,
          min: 0,
          step: 1,
          label: 'From'
        },
        {
          model: 'timeTo',
          max: 24,
          min: 12,
          step: 1,
          label: 'To'
        },
        {
          model: 'timeStep',
          max: 120,
          min: 10,
          step: 10,
          label: 'Step'
        }
      ];
    }
  },
  watch: {
    showLeftPanel(newVal) {
      if (!newVal) {
        this.showBottomNav(false); // hide bottom nav when center panel is opened
      } else {
        this.showBottomNav(true); // show bottom nav when left panel is opened
      }
    },
    currentCalendar(newVal){
      this.cleanup();
      if(newVal.type === CALENDAR_TYPES.DSS){
        this.initializeDSS();
      }else if (newVal.type === CALENDAR_TYPES.OD){
        this.initializeOD();
      }else if (newVal.type === CALENDAR_TYPES.GOOGLE){
        this.initializeGOOGLE();
      }
    }
  },
  methods: {
    ...mapMutations("window", ["showBottomNav"]),
    ...mapActions("calendar", ["getDSSEvents","fetchODAppointments","fetchClinics","getEventById","addEventMember","updateEventTime"]),
    ...mapMutations("calendar",["changeLoading"]),

    toggleLeftPanel() {
      this.showLeftPanel = !this.showLeftPanel;
    },
    handleDateClick: function (arg) {
      alert("date click! " + arg.dateStr);
    },
    editCalendar(){
      this.openEventForm = true;
      this.isCalendar = true;
      this.isEvent = false;
      this.isEdit = true;
    },
    initializeDSS(){
        this.editableEvents = this.canEditCalendar;
        if(this.canEditCalendar){
          this.editableEvents = { create: true, resize: true,  drag: true, delete: true };
        }
        this.timeStep= 30;
        this.currentCalendar.calendar_members.forEach((member) => {
          this.schedules.push({
            id: member.user_id,
            label: this.getUserName(member.user_id),
            color: this.hashedColor(this.getUserName(member.user_id)),
            role : member.role
          });
        });
        this.getDSSEvents(this.selectedDate);
    },
    async initializeOD(){
       try {
        this.changeLoading(true);
        this.editableEvents = false
        // fetch clinics
        await this.fetchClinics();

        //fetch Operatories
        let operatories = await OdService.getOperatories();
        operatories.data.forEach((op) => {
          if (op.IsHidden === "true") return; // Skip hidden operatories
          this.schedules.push({
            id: `${op.OperatoryNum}_${op.ClinicNum}`,
            label: op.OpName,
            color: this.hashedColor(this.clinics[op.ClinicNum].clinicName),
            clinicNum: op.ClinicNum,
          });
        });
        this.schedules.sort((a, b) => a.clinicNum - b.clinicNum);
        // fetch appointments for the current date
        await this.fetchODAppointments(this.selectedDate);
        this.timeFrom = this.dayjs(this.scheduleEvents[0].start).hour();
      } catch (error) {
        this.changeLoading(false);
         console.error("Error fetching operatories:", error);
      }
    },
    async handleViewChange(event) {
      this.selectedDate = event.cellDates[0].startFormatted;
      if(this.isDSSCalendar){
          this.selectedAuthors = [];
          await this.getDSSEvents(this.selectedDate)
      }else if(this.isODCalendar){
          this.selecteddoctors = [];
          await this.fetchODAppointments(this.selectedDate);
          this.timeFrom = this.dayjs(this.scheduleEvents[0]?.start).hour() || 6;
      }
      console.log("In view change",this.selectedDate);
      
    },
    async handleDeleteEvent(event){
        try {
          await CalendarService.deleteEvent(event.calendar_id, event.event_id);
        } catch (error) {
          console.error(error);
          this.getEventById({calendar_id: event.calendar_id, event_id: event.event_id});
        }
        
    },
    eventClicked(event){
      this.selectedEvent = { ...event.event };
      if(this.isODCalendar){
        this.showAptDetails = true;
      }else if(this.isDSSCalendar){
        this.openEventForm = true;
        this.isCalendar = false;
        this.isEvent = true;
        this.isEdit = true;
      }
    },
    createEvent(event){
      this.newEvent = event;
      this.openEventDialog();
    },
    handleEventResize(event){
       let fromDate = this.dayjs(event.event.start).format("YYYY-MM-DD");
        let fromTime = this.dayjs(event.event.start).format("HH:mm");
        let toTime = this.dayjs(event.event.end).format("HH:mm");
        const startTime = this.dayjs(`${fromDate} ${fromTime}`).valueOf();
        const endTime = this.dayjs(`${fromDate} ${toTime}`).valueOf();
        const data = {
          start_time : startTime,
          end_time : endTime,
          timezone : event.event.timezone
        }
        this.updateEventTime({event_id : event.event.event_id, data : data});
    },
    handleEventDropped(event){
      if(event.event.schedule == event.originalEvent.schedule){
        this.handleEventResize(event);
      }else{
        const data = {participants : [{"id": event.event.schedule, type: "user"}], type : "add"};
        this.addEventMember({event_id : event.event.event_id, data : data});
      }
    },
    openEventDialog(){
      this.openEventForm = true;
      this.isCalendar = false;
      this.isEvent = true;
      this.isEdit = false;
    },
    cleanup(){
      this.schedules= [];
      this.selectedResource = [];
      this.selecteddoctors = [];
      this.selectedAuthors = [];
      this.timeFrom = 0;
      this.timeTo = 24;
      this.timeStep = 60;      
      this.editableEvents = false;
    },
    unknownOperatory(clinicNum) {
      if (!this.schedules.some(schedule => schedule.clinicNum === clinicNum && schedule.label === "Unknown")) {
           this.schedules.push({
             id: `0_${clinicNum}`,
             label: "Unknown",
             color: this.hashedColor(this.clinics[clinicNum].clinicName),
             clinicNum: clinicNum,
           });
           this.schedules.sort((a, b) => a.clinicNum - b.clinicNum);
      }
    },
  },
  mounted() {
    EventBus.$on("add-event", () => {
        this.openEventDialog();
    });
    EventBus.$on("add-calendar", () => {
      this.openEventForm = true;
      this.isCalendar = true;
      this.isEvent = false;
      this.isEdit = false;
    });
    EventBus.$on("select-date", (date) => {
      this.selectedDate = date;
    });
    EventBus.$on("close-left-panel", (date) => {
      this.showLeftPanel = false;
    });
    EventBus.$on("calenderMemberAdded", (data) => {
      this.schedules.push({
        id: data.user_id,
        label: this.getUserName(data.user_id),
        color: this.hashedColor(this.getUserName(data.user_id)),
        role : data.role
      });
    });
    EventBus.$on("calenderMemberRemoved", (userId) => {
      this.schedules = this.schedules.filter(schedule => schedule.id != userId);
    });
    EventBus.$on("unknownOperatory", (clinicNum) => {
      this.unknownOperatory(clinicNum);
    });

    if (Capacitor.getPlatform() === 'android') {
   	  setCustomBackHandler(() => {
	      if(!this.showLeftPanel){
	      		 this.toggleLeftPanel();
	      }else{
	      	useDefaultBackHandler(router);
	      }
     });
   }
  },
  beforeUnmount(){
    EventBus.$off("add-event");
    EventBus.$off("add-calendar");
    EventBus.$off("select-date");
    EventBus.$off("close-left-panel");
    EventBus.$off("calenderMemberAdded");
    EventBus.$off("calenderMemberRemoved");
    EventBus.$off("unknownOperatory");
    this.cleanup();
    this.$store.commit('calendar/currentCalendar', {});
    this.$store.commit('calendar/calendarList', []);
    this.$store.commit('calendar/scheduleEvents', []);
    this.$store.commit('calendar/doctors', {});
    this.$store.commit('calendar/clinics', {});
    this.$store.commit('calendar/authors', {});
    this.clearCustomBackHandler;
  }
};
</script>
<style scoped>
.container {
  display: flex;
  flex-direction: row;
  height: 100%;
  align-content: space-around;
  align-items: flex-start;
}

.container > * {
  margin: 0 1px;
}

.container.mobile {
  flex-direction: column;
}

.left-panel.full-width {
  flex: 1;
  width: 100%;
}

.center-panel.full-width {
  flex: 1;
  width: 100%;
}

.filter-select {
  min-height: 30px;
  max-height: 30px;
  min-width: 160px;
  max-width: 230px;
}
.filter-select-mobile {
  min-height: 30px;
  max-height: 30px;
  min-width: 10px;
  max-width: 160px;
}

</style>
